import type {
  TopologyFaceBoundaryRuntimeServiceDependencies,
  TopologyFaceBoundaryRuntimeServicePort,
} from '../../../../../types/reference/sketch-entity-reference/TopologyFaceBoundaryRuntimeService';
import type {
  TopologyFaceBoundaryMemberEvidence,
  TopologyFaceReferenceSourceDescriptor,
  TopologyFaceRelocationCandidate,
  TopologyFaceRelocationHost,
} from '../../../../../types/reference/sketch-entity-reference/TopologyFaceReferenceSource';
import type {
  TopologyEdgeRelocationCandidate,
} from '../../../../../types/reference/sketch-entity-reference/TopologyEdgeRelocationService';
import type {
  TopologyFaceBoundaryRuntimeResult,
  TopologyEdgeSelectionRuntimeResult,
  TopologyWireBoundaryIndexSnapshot,
  TopologyWireRuntimeCandidate,
  QueryConvertEntitiesInnerLoopByEdgeResult,
} from '../../../../../types/reference/sketch-entity-reference/TopologyFaceBoundaryRuntime';
import type {
  TopologyEdgeSourceDescriptor,
} from '../../model/ExternalGeometrySourceDescriptor';
import {
  resolveExternalGeometrySourceDescriptorKey,
  resolveTopologySourceMemberScopeKey,
} from '../../model/ExternalGeometrySourceDescriptor';
import { TopologyFaceRelocationService } from './TopologyFaceRelocationService';
import { TopologyEdgeSourceDescriptorFactory } from './TopologyEdgeSourceDescriptorFactory';
import { TopologyFaceRegionBoundaryResolver } from './TopologyFaceRegionBoundaryResolver';
import {TopologyOwnerRuntimeHostFactory} from './TopologyOwnerRuntimeHostFactory';
import type {
  TopologyOwnerRuntimeHost,
} from '../../../../../types/reference/sketch-entity-reference/TopologyOwnerRuntimeResolver';

/**
 * Convert Entities 主线程 Face/Wire 运行时解析服务。
 */
export class TopologyFaceBoundaryRuntimeService
implements TopologyFaceBoundaryRuntimeServicePort {
  /** Face 稳定重定位服务。 */
  private readonly faceRelocationService = new TopologyFaceRelocationService();
  /** 当前 edge 描述统一工厂。 */
  private readonly edgeDescriptorFactory = new TopologyEdgeSourceDescriptorFactory();
  /** 复用 Scene/checkpoint 的坐标转换和几何适配规则。 */
  private readonly topologyHostFactory = new TopologyOwnerRuntimeHostFactory();
  /** 相邻或拆分后代面的区域外边界算法。 */
  private readonly faceRegionBoundaryResolver = new TopologyFaceRegionBoundaryResolver();
  /** 显示几何与候选 BRep 世界坐标的一致性容差，仅排除索引错配，不用于修改几何。 */
  private static readonly sourceGeometryTolerance = 1e-6;

  /**
   * @param dependencies owner host 与 Worker 查询端口。
   */
  public constructor(
    private readonly dependencies: TopologyFaceBoundaryRuntimeServiceDependencies,
  ) {}

  /**
   * @inheritdoc
   */
  public async resolveEdgeSelection(
    edgeSource: TopologyEdgeSourceDescriptor,
    intentId?: string,
  ): Promise<TopologyEdgeSelectionRuntimeResult | null> {
    // 先确认逻辑 owner 在当前场景仍可解析，避免为已删除来源发起无意义 Worker 查询。
    const sourceHost = this.dependencies.resolveTopologyOwnerHost(edgeSource);
    if (!sourceHost) {
      return null;
    }
    const sourceEdge = sourceHost.getEdgeByIndex(edgeSource.locator.edgeIndex);
    // 初始组合 host 可能没有唯一边；仍需让 Worker 先确认结果成员，不能提前截断精确路线。
    const sourceMetadata = sourceEdge?.featureLine?.getOwningResultMesh()?.getFeatureMetadata();
    const canUseDisplayFallback = Boolean(sourceEdge && (sourceMetadata?.source === 'imported-topo-sidecar'
      || typeof sourceMetadata?.topologySidecarEntryKey === 'string'));
    const queryInnerLoopByEdge = this.dependencies.queryTopologyInnerLoopByEdge;
    if (!queryInnerLoopByEdge) {
      if (canUseDisplayFallback) return this.createDisplayEdgeSelection(edgeSource, sourceEdge, intentId);
      throw new Error('当前引用运行时不支持 Convert Entities canonical 单边查询');
    }
    // 单次 Worker 查询同时返回 canonical 单边身份与可选 InnerWire，避免外环边产生二次往返。
    let queryResult: QueryConvertEntitiesInnerLoopByEdgeResult;
    try {
      queryResult = await queryInnerLoopByEdge({ owner: edgeSource.owner, edgeIndex: edgeSource.locator.edgeIndex });
    } catch (error) {
      // 仅“没有对应 Body/Edge”属于采样回退条件；超时、取消和其他内核故障仍交由原错误链处理。
      const message = error instanceof Error ? error.message : String(error);
      if (canUseDisplayFallback && /无法解析 topology (?:owner|edge)|Body 尚未绑定可定位的 BRep Shape|未找到匹配的 Body|Body BRep 匹配不唯一|Body BRep 无可用拓扑/.test(message)) {
        return this.createDisplayEdgeSelection(edgeSource, sourceEdge, intentId);
      }
      throw error;
    }
    if (!queryResult.topology) {
      if (canUseDisplayFallback) return this.createDisplayEdgeSelection(edgeSource, sourceEdge, intentId);
      return null;
    }
    const currentOwner = {
      ...structuredClone(edgeSource.owner),
      resultObjectId: queryResult.resolvedResultObjectId,
    };
    const currentEdgeSource: TopologyEdgeSourceDescriptor = {
      ...structuredClone(edgeSource),
      owner: currentOwner,
    };
    // Worker 已确认具体结果对象后只读取该精确场景成员，禁止重新聚合同血缘分裂成员。
    const currentOwnerHost = this.resolveCurrentOwnerHost(
      currentEdgeSource,
    );
    const sceneEdge = currentOwnerHost?.getEdgeByIndex(edgeSource.locator.edgeIndex);
    if (!sceneEdge) {
      if (canUseDisplayFallback) return this.createDisplayEdgeSelection(edgeSource, sourceEdge, intentId);
      return null;
    }
    // 同批 Worker 身份和几何一起进入转换，场景只提供放置变换及显示代理。
    const currentEdge = this.mergeCanonicalEdgeIdentity(
      sceneEdge,
      queryResult.resolvedResultObjectId,
      queryResult.topology,
    );
    if (!currentEdge) {
      if (canUseDisplayFallback) return this.createDisplayEdgeSelection(edgeSource, sourceEdge, intentId);
      return null;
    }
    // 后端索引尚未形成可靠映射：查到同号 Edge 还不够，几何无法核对时保持原显示边身份并拟合。
    if (canUseDisplayFallback && !this.matchesDisplayEdgeGeometry(sourceEdge!, currentEdge)) {
      return this.createDisplayEdgeSelection(edgeSource, sourceEdge, intentId);
    }
    const edgeRuntime = {
      edgeSource: this.edgeDescriptorFactory.create({
        owner: currentOwner,
        edge: currentEdge,
      }),
      edgeCandidate: currentEdge,
    };
    if (!queryResult.match) {
      return {...edgeRuntime, innerLoop: null};
    }
    // 点击边属于直接 InnerWire 时复用同批 topology，一次性建立完整环选择结果。
    const currentFaceSource: TopologyFaceReferenceSourceDescriptor = {
      owner: currentOwner,
      locator: structuredClone(queryResult.match.faceLocator),
    };
    const innerWire = this.createWireRuntimeCandidate(
      currentOwnerHost,
      currentFaceSource,
      queryResult.match.innerWire,
      queryResult.topology,
      intentId,
    );
    return innerWire
      ? {
          ...edgeRuntime,
          innerLoop: {
            faceSource: currentFaceSource,
            innerWireIndex: queryResult.match.innerWireIndex,
            innerWire,
          },
        }
      : null;
  }

  /**
   * @inheritdoc
   *
   * Face 身份与 Wire 数据完整性分层处理：只有前者失败返回 null，后者失败抛错。
   */
  public async resolve(
    faceSource: TopologyFaceReferenceSourceDescriptor,
    intentId?: string,
    boundaryEvidence: readonly TopologyFaceBoundaryMemberEvidence[] = [],
    innerWirePolicy: 'best-effort' | 'all-wires' = 'best-effort',
  ): Promise<TopologyFaceBoundaryRuntimeResult | null> {
    const ownerHost = this.dependencies.resolveTopologyOwnerHost(
      this.createOwnerProbeDescriptor(faceSource),
    );
    if (!ownerHost?.getFaceByIndex || !ownerHost.listFaces) {
      return null;
    }
    const faceHost: TopologyFaceRelocationHost = {
      getFaceByIndex: ownerHost.getFaceByIndex,
      listFaces: ownerHost.listFaces,
    };
    const currentResultObjectId = faceSource.owner.resultObjectId;
    const exactCurrentHost = innerWirePolicy === 'all-wires' && currentResultObjectId
      ? this.dependencies.resolveExactTopologyOwnerHost?.(
          this.createOwnerProbeDescriptor(faceSource),
        )
      : null;
    const exactCurrentFace = exactCurrentHost?.getFaceByIndex?.(
      faceSource.locator.faceIndex,
    );
    /*
     * “选择所有内环”复用首次选面已经确认的当前结果成员。导入模型的场景
     * sidecar 身份可能不同于 Worker canonical 身份，但在同一 resultObjectId 内，
     * faceIndex 仍是两种载荷的原子关联地址；禁止因此重新执行跨历史面猜测。
     */
    const reusableCurrentFace = currentResultObjectId
      && exactCurrentFace?.topologyMemberId === currentResultObjectId
      ? exactCurrentFace
      : null;
    const relocation = reusableCurrentFace
      ? {status: 'resolved' as const, faces: [reusableCurrentFace]}
      : this.faceRelocationService.relocate(
          faceSource,
          faceHost,
          boundaryEvidence,
        );
    if (relocation.status !== 'resolved') {
      return null;
    }
    const faceBoundaries = await Promise.all(relocation.faces.map(async (face) => {
      const scopedOwner = {
        ...structuredClone(faceSource.owner),
        ...(face.topologyMemberId
          ? { resultObjectId: face.topologyMemberId }
          : {}),
      };
      const queryResult = await this.dependencies.queryTopologyFaceBoundaries({
        owner: scopedOwner,
        faceIndices: [face.faceIndex],
      });
      const boundary = queryResult.boundaries.find(
        item => item.faceIndex === face.faceIndex,
      );
      if (!boundary || !queryResult.topology) {
        throw new Error(
          `来源面已定位，但当前 Face/Wire canonical topology 不完整: faceIndex=${face.faceIndex}`,
        );
      }
      const canonicalFace = queryResult.topology.faces.find(
        candidate => candidate.index === face.faceIndex,
      );
      if (!canonicalFace) {
        throw new Error(
          `来源面已定位，但 canonical topology 缺少对应 Face: faceIndex=${face.faceIndex}`,
        );
      }
      const currentOwner = {
        ...scopedOwner,
        resultObjectId: queryResult.resolvedResultObjectId,
      };
      const scopedFaceSource: TopologyFaceReferenceSourceDescriptor = {
        owner: currentOwner,
        locator: {
          faceIndex: canonicalFace.index,
          ...(canonicalFace.semanticName
            ? {semanticName: canonicalFace.semanticName}
            : {}),
          persistentRef: canonicalFace.ref,
          subEntityId: canonicalFace.subEntityId,
          ...(canonicalFace.surfaceKind
            ? {surfaceKind: canonicalFace.surfaceKind}
            : {}),
          owningFeatureId: canonicalFace.owningFeatureId,
        },
      };
      const scopedHost = this.resolveCurrentOwnerHost(
        this.createOwnerProbeDescriptor(scopedFaceSource),
      );
      if (!scopedHost) {
        throw new Error(
          `来源面已定位，但无法读取当前结果成员的场景边界: faceIndex=${face.faceIndex}`,
        );
      }
      const outerWire = this.createWireRuntimeCandidate(
        scopedHost,
        scopedFaceSource,
        boundary.outerWire,
        queryResult.topology,
        intentId,
      );
      const innerWires = boundary.innerWires.map(wire => (
        this.createWireRuntimeCandidate(
          scopedHost,
          scopedFaceSource,
          wire,
          queryResult.topology,
          intentId,
        )
      ));
      if (!outerWire) {
        throw new Error(
          `来源面已定位，但当前 Wire 成员无法完整映射到场景边: faceIndex=${face.faceIndex}`,
        );
      }
      if (innerWirePolicy === 'all-wires' && innerWires.some(wire => !wire)) {
        throw new Error(
          `来源面已定位，但当前 Wire 成员无法完整映射到场景边: faceIndex=${face.faceIndex}`,
        );
      }
      return {
        faceSource: scopedFaceSource,
        outerWire,
        innerWires: innerWires.filter(
          (wire): wire is TopologyWireRuntimeCandidate => Boolean(wire),
        ),
        innerWireCount: boundary.innerWires.length,
      };
    }));
    const resolvedBoundaries = faceBoundaries;
    const currentFaceSource = resolvedBoundaries.length === 1
      ? structuredClone(resolvedBoundaries[0]!.faceSource)
      : this.refreshFaceSource(faceSource, relocation.faces);
    return {
      faceSource: currentFaceSource,
      outerWire: this.mergeFaceBoundaryCandidates(resolvedBoundaries),
      innerWires: resolvedBoundaries.flatMap(boundary => boundary.innerWires),
      innerWireCount: resolvedBoundaries.reduce(
        (count, boundary) => count + boundary.innerWireCount,
        0,
      ),
    };
  }

  /**
   * 把 Worker Wire 索引快照收口为当前可投影的完整边候选。
   *
   * @param ownerHost 当前 topology owner host。
   * @param faceSource Wire 所属的当前 Face。
   * @param wire Worker 返回的完整 Wire 索引。
   * @param canonicalTopology 与 Wire 来自同一 Worker Shape 的 canonical topology 身份子集。
   * @param intentId 可选复合选择意图 ID。
   * @returns 所有成员均可解析时返回完整 Wire，否则返回 null。
   */
  private createWireRuntimeCandidate(
    ownerHost: TopologyOwnerRuntimeHost,
    faceSource: TopologyFaceReferenceSourceDescriptor,
    wire: TopologyWireBoundaryIndexSnapshot,
    canonicalTopology: RuntimeTopologySnapshot,
    intentId?: string,
  ): TopologyWireRuntimeCandidate | null {
    const topologyMemberId = faceSource.owner.resultObjectId;
    if (!topologyMemberId) {
      return null;
    }
    const edges = wire.edgeIndices.map((edgeIndex) => {
      const sceneEdge = ownerHost.getEdgeByIndex(edgeIndex);
      if (!sceneEdge) {
        return null;
      }
      return this.mergeCanonicalEdgeIdentity(
        sceneEdge,
        topologyMemberId,
        canonicalTopology,
      );
    });
    if (edges.some(edge => !edge)) {
      return null;
    }
    const edgeCandidates = edges as NonNullable<typeof edges[number]>[];
    return {
      runtimeId: wire.runtimeId,
      edgeCandidates,
      edgeSources: edgeCandidates.map(edge => this.edgeDescriptorFactory.create({
        owner: faceSource.owner,
        edge,
        originSelectionIntentId: intentId,
      })),
    };
  }

  /**
   * 解析 Worker 已确认结果对象对应的场景 host。
   *
   * live Viewer 提供精确成员端口，避免同血缘分裂成员的重复局部 index 重新形成歧义；
   * checkpoint runtime 没有场景成员概念，继续复用既有 owner host。
   *
   * @param descriptor 已携带当前 `resultObjectId` 的来源描述。
   * @returns 精确场景成员或 checkpoint owner host；无法解析时返回 `null`。
   */
  private resolveCurrentOwnerHost(
    descriptor: TopologyEdgeSourceDescriptor,
  ): TopologyOwnerRuntimeHost | null {
    const exactResolver = this.dependencies.resolveExactTopologyOwnerHost;
    return exactResolver
      ? exactResolver(descriptor)
      : this.dependencies.resolveTopologyOwnerHost(descriptor);
  }

  /**
   * 将同一 BRep 查询的身份和几何一起转换到场景世界坐标。
   *
   * 场景只提供坐标变换及显示代理；建模数值必须来自同批 Worker，
   * 否则共享端点身份对应的直线、圆弧会混入不同精度的坐标。
   * @param sceneEdge 当前成员内的显示候选。
   * @param topologyMemberId 已确认的结果成员。
   * @param topology 同批 BRep 查询得到的拓扑与几何。
   * @returns 完整候选；Worker 缺少边时返回 null，不以旧代理补造。
   */
  private mergeCanonicalEdgeIdentity(
    sceneEdge: TopologyEdgeRelocationCandidate,
    topologyMemberId: string,
    topology: RuntimeTopologySnapshot,
  ): TopologyEdgeRelocationCandidate | null {
    const candidate = this.topologyHostFactory.create({
      topology, topologyMemberId,
      transformPoint: sceneEdge.toWorldPoint,
    }).getEdgeByIndex(sceneEdge.edgeIndex);
    if (!candidate) return null;
    return {...candidate, featureLine: sceneEdge.featureLine};
  }

  /**
   * 把当前 Body 的显示边交给既有直线/采样解析器，不伪造 BRep 身份或内环。
   * @param source 原始选择的 owner。
   * @param edge 同一 owner 下的显示候选。
   * @param intentId 当前选择意图。
   * @returns 单边结果；数据不足时给出可定位错误。
   */
  private createDisplayEdgeSelection(
    source: TopologyEdgeSourceDescriptor,
    edge: TopologyEdgeRelocationCandidate | null,
    intentId?: string,
  ): TopologyEdgeSelectionRuntimeResult | null {
    if (!edge) return null;
    const descriptor = this.edgeDescriptorFactory.create({ owner: source.owner, edge, originSelectionIntentId: intentId });
    if (descriptor.locator.resolutionLevel === 'unsupported') {
      throw new Error(`当前模型边缺少可用的曲线参数或采样点，无法转换实体：edgeIndex=${edge.edgeIndex}`);
    }
    return { edgeSource: descriptor, edgeCandidate: edge, innerLoop: null };
  }

  /**
   * 核对相同 index 的查询结果是否与用户选择的显示边一致。
   *
   * 曲线采样密度或顺序无法直接核对时保守回退拟合，不做最近邻猜测；
   * 该检查只能排除明显错配，后端稳定 Edge 映射仍是最终需要补齐的契约。
   * @param display 当前显示候选。
   * @param queried BRep 查询候选。
   * @returns 类型和正向/反向点序列都可核对时返回 true。
   */
  private matchesDisplayEdgeGeometry(display: TopologyEdgeRelocationCandidate, queried: TopologyEdgeRelocationCandidate): boolean {
    if (display.curveKind !== queried.curveKind) return false;
    const left = display.worldLine ? [display.worldLine.start, display.worldLine.end] : display.sampledPointWorlds;
    const right = queried.worldLine ? [queried.worldLine.start, queried.worldLine.end] : queried.sampledPointWorlds;
    if (!left || !right || left.length < 2 || left.length !== right.length) return false;
    const tolerance = TopologyFaceBoundaryRuntimeService.sourceGeometryTolerance;
    let forward = true;
    let reverse = true;
    for (let index = 0; index < left.length; index += 1) {
      for (let axis = 0; axis < 3; axis += 1) {
        const value = left[index]![axis]!;
        forward = forward && Math.abs(value - right[index]![axis]!) <= tolerance;
        reverse = reverse && Math.abs(value - right[right.length - 1 - index]![axis]!) <= tolerance;
      }
      if (!forward && !reverse) return false;
    }
    return forward || reverse;
  }

  /**
   * 把同一逻辑来源面的多个物理外环收口为区域外边界。
   *
   * @param boundaries 已按物理面稳定顺序解析的 Face 边界。
   * @returns 消除内部共享边后的完整逻辑外边界。
   */
  private mergeFaceBoundaryCandidates(
    boundaries: readonly TopologyFaceBoundaryRuntimeResult[],
  ): TopologyWireRuntimeCandidate {
    const regionResult = this.faceRegionBoundaryResolver.resolve({
      faces: boundaries.map(boundary => ({
        face: boundary,
        ownerKey: resolveTopologySourceMemberScopeKey(boundary.faceSource.owner),
        edges: boundary.outerWire.edgeSources.flatMap((source, index) => {
          const edgeCandidate = boundary.outerWire.edgeCandidates[index];
          return edgeCandidate
            ? [{
                key: resolveExternalGeometrySourceDescriptorKey(source),
                edge: {source, edgeCandidate},
              }]
            : [];
        }),
      })),
    });
    const boundaryMembers = regionResult.regions.flatMap(region => region.boundaryEdges);
    return {
      runtimeId: boundaries.map(boundary => boundary.outerWire.runtimeId).join('|'),
      edgeSources: boundaryMembers.map(member => member.edge.source),
      edgeCandidates: boundaryMembers.map(member => member.edge.edgeCandidate),
    };
  }

  /**
   * 用当前物理后代刷新逻辑面来源。
   *
   * 多后代继续保留语义来源和实体血缘；单后代则写回精确物理面与结果对象。
   */
  private refreshFaceSource(
    source: TopologyFaceReferenceSourceDescriptor,
    faces: readonly TopologyFaceRelocationCandidate[],
  ): TopologyFaceReferenceSourceDescriptor {
    const face = faces[0];
    if (!face) {
      return structuredClone(source);
    }
    if (faces.length > 1) {
      const {
        resultObjectId: _resultObjectId,
        ...logicalOwner
      } = structuredClone(source.owner);
      return {
        owner: logicalOwner,
        locator: {
          ...structuredClone(source.locator),
          semanticName: source.locator.semanticName ?? face.semanticName,
          surfaceKind: source.locator.surfaceKind ?? face.surfaceKind,
          owningFeatureId: source.locator.owningFeatureId ?? face.owningFeatureId,
        },
      };
    }
    return {
      owner: {
        ...structuredClone(source.owner),
        ...(face.topologyMemberId
          ? { resultObjectId: face.topologyMemberId }
          : {}),
      },
      locator: {
        faceIndex: face.faceIndex,
        semanticName: face.semanticName,
        persistentRef: face.persistentRef,
        subEntityId: face.subEntityId,
        surfaceKind: face.surfaceKind,
        owningFeatureId: face.owningFeatureId,
      },
    };
  }

  /**
   * 为只按 owner 解析的 host 构建最小 edge probe。
   */
  private createOwnerProbeDescriptor(
    faceSource: TopologyFaceReferenceSourceDescriptor,
  ): TopologyEdgeSourceDescriptor {
    return {
      kind: 'topology-edge',
      owner: structuredClone(faceSource.owner),
      locator: {
        edgeIndex: 0,
        resolutionLevel: 'unsupported',
      },
    };
  }
}
